import { useEffect, useRef, useState } from 'react';
import { useToast } from '../components/ui';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { isGuestUser } from '../auth/guest';
import { useStartupGate } from '../app/startupGateContext';
import { AttributionDialog } from './AttributionDialog';
import { acquisitionStatus, answerAttribution, attributionPlatform, claimAttributionPrompt, isAttributionOverview, quietAttributionMoment } from './acquisition';

export function AttributionHost() {
  const { user, status } = useAuth();
  if (status !== 'authed' || !user || isGuestUser(user) || user.profile_incomplete === true || user.max_age_level !== 'age_18' || !attributionPlatform()) return null;
  return <AccountAttributionHost key={user.userId} id={user.userId} />;
}
function AccountAttributionHost({ id }: { id: number }) {
  const location = useLocation();
  const { toast } = useToast();
  const gate = useStartupGate();
  const platform = attributionPlatform();
  const started = useRef(Date.now());
  const current = useRef(true);
  const claimed = useRef(false);
  const [version, setVersion] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { current.current = true; return () => { current.current = false; }; }, []);
  useEffect(() => {
    let cancelled = false;
    let pending = false;
    const quiet = () => quietAttributionMoment(location.pathname, gate, started.current, Date.now());
    const check = async () => {
      if (pending || claimed.current || !quiet()) return;
      pending = true;
      try {
        const state = await acquisitionStatus();
        if (cancelled || !state.prompt || !quiet()) return;
        const result = await claimAttributionPrompt(id);
        if (cancelled) return;
        claimed.current = true;
        if (result.claimed && quiet()) setVersion(result.text_version);
      } catch { /* Netzwerkfehler: kein Zugriff, nächste ruhige Prüfung abwarten. */ }
      finally { pending = false; }
    };
    void check();
    const timer = window.setInterval(() => { void check(); }, 15000);
    return () => { cancelled = true; window.clearInterval(timer); };
  }, [gate, id, location.pathname]);
  if (!version || !platform || gate !== 'none' || !isAttributionOverview(location.pathname)) return null;
  return <AttributionDialog platform={platform} busy={busy} error={false} onAnswer={choice => {
    if (busy) return;
    setBusy(true);
    void answerAttribution(id, choice, version, () => current.current)
      .then(() => { if (current.current) setVersion(null); })
      .catch(() => { if (current.current) { toast({ tone: 'error', titleKey: 'attribution.failed' }); setVersion(null); } })
      .finally(() => { if (current.current) setBusy(false); });
  }} />;
}
